"use client";

import React from "react";
import { Activity, ClipboardCheck, MessageCircle, Stethoscope, TrendingUp, ShieldCheck, PlusCircle, CheckCircle2, Mountain, Crown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
const includedItems = [{
  icon: ClipboardCheck,
  title: "Comprehensive Lab Review",
  description: "We evaluate testosterone, key health markers, and safety labs to determine whether TRT is clinically appropriate."
}, {
  icon: Stethoscope,
  title: "Physician Consultation",
  description: "Meet directly with Dr. Flores to review symptoms, labs, goals, risks, and whether testosterone therapy makes medical sense for you."
}, {
  icon: Activity,
  title: "Personalized TRT Plan",
  description: "If treatment is appropriate, your protocol is customized around your labs, symptoms, lifestyle, and safety monitoring needs."
}, {
  icon: MessageCircle,
  title: "Ongoing Monthly Support",
  description: "You receive continued follow-up, dose adjustments when needed, and physician-led oversight as your care progresses."
}];
const symptomItems = ["Low energy despite sleep or caffeine", "Loss of drive, motivation, or confidence", "Decreased libido or weaker sexual performance", "More belly fat and harder body recomposition", "Slower workout recovery or declining strength", "Feeling like you are falling behind at work and home"];
const packages = [{
  name: "Basecamp",
  price: "$150",
  period: "/month",
  label: "Start Here",
  icon: Mountain,
  description: "Physician-led testosterone care for qualified men who want clear answers, proper monitoring, and a focused starting point.",
  features: ["Symptom and medical history review", "Comprehensive lab review", "Physician consultation with Dr. Flores", "Personalized TRT plan when appropriate", "Ongoing monthly support"],
  highlight: true
}, {
  name: "Ascent",
  price: "$325",
  period: "/month",
  label: "Upgrade When Needed",
  icon: TrendingUp,
  description: "For men who need additional support with metabolism, recovery, weight, or energy beyond testosterone care alone.",
  features: ["Everything in Basecamp", "Medical weight loss support when appropriate", "NAD+ or vitamin injection support", "Recovery and performance support", "Expanded optimization strategy"],
  highlight: false
}, {
  name: "Summit",
  price: "$595",
  period: "/month",
  label: "Highest Support",
  icon: Crown,
  description: "For men who want the most comprehensive physician-led support, advanced monitoring, and a higher-touch optimization plan.",
  features: ["Everything in Ascent", "Advanced cardiometabolic lab review", "More complex hormone and health optimization", "Priority access and closer follow-up", "Comprehensive performance and longevity strategy"],
  highlight: false
}];
const addOnServices = ["Medical weight loss / GLP-1 support", "NAD+ therapy", "Vitamin injections", "Recovery and performance support", "Advanced cardiometabolic labs", "Performance and longevity optimization"];
export default function BasecampTRTFunnel() {
  React.useEffect(() => {
    const calendlyCssHref = "https://assets.calendly.com/assets/external/widget.css";
    const calendlyScriptSrc = "https://assets.calendly.com/assets/external/widget.js";
    if (!document.querySelector(`link[href="${calendlyCssHref}"]`)) {
      const link = document.createElement("link");
      link.href = calendlyCssHref;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${calendlyScriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = calendlyScriptSrc;
      script.type = "text/javascript";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const handleCalendlyClick = () => {
    if ((window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  return <section id="basecamp-trt" className="py-12 md:py-16 bg-background text-foreground overflow-hidden">
      <div className="container px-4 sm:px-6 lg:px-8">
        {/* Hero / Primary Offer */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-14">
          <div className="min-w-0">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-3 py-1.5 sm:px-4 sm:py-2 mb-5">
              <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-primary leading-snug">
                Basecamp TRT Package — $150/month
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground leading-tight mb-5">
              Physician-Led Testosterone Care for Men Who Feel Like They’re
              Running on Empty
            </h1>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-6">
              For Texas men in their 30s, 40s, and 50s who are tired, gaining
              weight, losing drive, or struggling to keep up with work,
              training, and family life, low testosterone may be part of the
              problem.
            </p>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-8">
              Basecamp TRT is IronPeak Men’s Health’s physician-led starting
              point: lab review, medical consultation, a personalized
              testosterone plan when clinically appropriate, and ongoing monthly
              support.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button size="lg" className="btn-primary w-full max-w-[340px] sm:w-auto sm:max-w-none whitespace-normal text-center leading-snug" onClick={handleCalendlyClick}>
                Book Your Free 30-Minute Consultation
              </Button>

              <div className="text-sm text-muted-dark leading-relaxed">
                <span className="font-semibold text-foreground">
                  $150/month
                </span>
                <br />
                Physician-led TRT care for qualified patients
              </div>
            </div>

            <p className="text-xs md:text-sm text-muted-dark leading-relaxed mt-5">
              Treatment is prescribed only when medically appropriate after a
              physician review of your symptoms, history, and lab results.
            </p>
          </div>

          <div className="bg-card border border-muted-light rounded-xl shadow-soft p-5 sm:p-6 md:p-8 min-w-0">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Does this sound familiar?
            </h2>

            <p className="text-base text-muted-dark leading-relaxed mb-6">
              You are still showing up for your job, your family, and your
              responsibilities — but it takes more effort than it used to.
            </p>

            <ul className="space-y-3">
              {symptomItems.map((item, i) => <li key={i} data-index={i} className="flex items-start text-base text-muted-dark leading-relaxed">
                  <CheckCircle2 className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>)}
            </ul>

            <div className="mt-6 rounded-lg bg-primary-light-bg p-4 border border-primary/20">
              <p className="text-sm md:text-base text-muted-dark leading-relaxed">
                These symptoms can have many causes. Our job is to evaluate your
                hormones and health markers carefully, then build a plan that
                makes sense medically.
              </p>
            </div>
          </div>
        </div>

        {/* What Is Included */}
        <div className="text-center max-w-4xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-snug">
            What’s Included in Basecamp TRT
          </h2>

          <p className="text-base md:text-lg text-muted-dark leading-relaxed">
            A focused, physician-led TRT package designed to help qualified men
            start safely, understand their numbers, and receive ongoing support
            without confusing tiers or unnecessary upsells.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-14">
          {includedItems.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={i} data-index={i} className="bg-card border border-muted-light rounded-xl shadow-soft p-5 sm:p-6 hover-lift min-w-0">
                <div className="w-12 h-12 bg-primary-light-bg rounded-lg flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-primary" />
                </div>

                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {item.title}
                </h3>

                <p className="text-sm md:text-base text-muted-dark leading-relaxed">
                  {item.description}
                </p>
              </div>;
        })}
        </div>

        {/* Price Anchor */}
        <div className="bg-card border border-muted-light rounded-xl shadow-soft p-5 sm:p-6 md:p-8 mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2 min-w-0">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Start With the Foundation: Basecamp TRT
              </h2>

              <p className="text-base md:text-lg text-muted-dark leading-relaxed">
                Instead of choosing from a long menu of men’s health services,
                start with the core question: are your symptoms connected to low
                testosterone or another measurable health issue? Basecamp TRT is
                built to answer that question and guide the next step.
              </p>
            </div>

            <div className="rounded-xl bg-primary-light-bg border border-primary/20 p-5 sm:p-6 text-center">
              <div className="text-sm font-semibold text-primary mb-1">
                Basecamp TRT
              </div>

              <div className="text-4xl md:text-5xl font-bold text-foreground mb-1">
                $150
              </div>

              <div className="text-sm text-muted-dark mb-4">per month</div>

              <Button size="lg" className="btn-primary w-full whitespace-normal text-center leading-snug" onClick={handleCalendlyClick}>
                Book Consultation
              </Button>
            </div>
          </div>
        </div>

        {/* Upgrade Path */}
        <div id="upgrade-path" className="mb-14">
          <div className="max-w-4xl mx-auto text-center mb-10 md:mb-12">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-3 py-1.5 sm:px-4 sm:py-2 mb-5">
              <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-primary leading-snug">
                Simple Starting Point. Clear Upgrade Path.
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-tight">
              Start With Basecamp. Upgrade Only If Your Goals and Labs Call for
              More.
            </h2>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed max-w-3xl mx-auto">
              Most men start with Basecamp TRT. If your symptoms, labs, or goals
              suggest you need additional support, we can build from there with
              higher-touch options.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
            {packages.map((pkg, index) => {
            const IconComponent = pkg.icon;
            return <div key={pkg.name} className={`relative rounded-2xl border shadow-soft p-5 md:p-6 flex flex-col min-w-0 ${pkg.highlight ? "bg-primary-light-bg border-primary/40" : "bg-card border-muted-light"}`}>
                  {pkg.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <div className="rounded-full bg-primary text-primary-foreground px-4 py-1 text-xs font-bold shadow-medium whitespace-nowrap">
                        Recommended Starting Point
                      </div>
                    </div>}

                  <div className="flex items-center justify-between gap-4 mb-5 mt-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                        {pkg.label}
                      </p>

                      <h3 className="text-2xl font-bold text-foreground">
                        {pkg.name}
                      </h3>
                    </div>

                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${pkg.highlight ? "bg-card" : "bg-primary-light-bg"}`}>
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                  </div>

                  <div className="mb-5">
                    <span className="text-4xl font-bold text-foreground">
                      {pkg.price}
                    </span>
                    <span className="text-muted-dark">{pkg.period}</span>
                  </div>

                  <p className="text-sm md:text-base text-muted-dark leading-relaxed mb-6">
                    {pkg.description}
                  </p>

                  <ul className="space-y-3 mb-6 flex-grow">
                    {pkg.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start gap-3 text-sm text-muted-dark leading-relaxed">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>

                  {index < packages.length - 1 && <div className="hidden lg:flex absolute top-1/2 -right-5 -translate-y-1/2 z-10">
                      <div className="w-10 h-10 rounded-full bg-background border border-muted-light shadow-soft flex items-center justify-center">
                        <ArrowRight className="w-5 h-5 text-primary" />
                      </div>
                    </div>}
                </div>;
          })}
          </div>

          <div className="max-w-4xl mx-auto mt-10 text-center">
            <div className="rounded-xl border border-muted-light bg-card p-5 md:p-6 shadow-soft">
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-3">
                Not Sure Which Package Fits?
              </h3>

              <p className="text-sm md:text-base text-muted-dark leading-relaxed mb-5">
                That is what the consultation is for. We start with your
                symptoms, labs, health history, and goals — then help you
                understand the right level of care.
              </p>

              <Button onClick={handleCalendlyClick} className="w-full max-w-[340px] sm:w-auto sm:max-w-none bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active shadow-medium hover:shadow-strong transition-all duration-150 px-5 sm:px-8 py-3 min-h-12 h-auto text-sm sm:text-base font-semibold rounded-md whitespace-normal text-center leading-snug">
                Book Your Free 30-Minute Consultation
              </Button>

              <p className="text-xs md:text-sm text-muted-dark leading-relaxed mt-4">
                Treatment is prescribed only when medically appropriate after
                physician review.
              </p>
            </div>
          </div>
        </div>

        {/* Add-ons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-14">
          <div className="min-w-0">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-muted-light bg-card px-3 py-1.5 sm:px-4 sm:py-2 mb-5">
              <PlusCircle className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-foreground leading-snug">
                Optional Add-On Services
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-snug">
              Need More Than TRT?
            </h2>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-6">
              Some men need additional help with weight, recovery, metabolic
              health, or performance. Those services are available when
              appropriate — but they do not replace the foundation.
            </p>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed">
              We start with Basecamp TRT because it gives you a clear medical
              entry point, then add services only when they fit your goals,
              labs, and clinical picture.
            </p>
          </div>

          <div className="bg-card border border-muted-light rounded-xl shadow-soft p-5 sm:p-6 min-w-0">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {addOnServices.map((service, i) => <li key={i} data-index={i} className="flex items-start text-sm md:text-base text-muted-dark leading-relaxed">
                  <div className="w-2 h-2 bg-secondary rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>{service}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <div className="bg-primary-light-bg rounded-xl p-5 sm:p-6 md:p-8 max-w-4xl mx-auto border border-primary/20">
            <TrendingUp className="w-10 h-10 text-primary mx-auto mb-4" />

            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Find Out If Low Testosterone Is Part of the Problem
            </h2>

            <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-6">
              If you are tired of guessing, start with a physician-led review.
              We will look at your symptoms, labs, health history, and goals —
              then help you understand whether TRT is appropriate.
            </p>

            <Button size="lg" className="btn-primary w-full max-w-[340px] sm:w-auto sm:max-w-none whitespace-normal text-center leading-snug" onClick={handleCalendlyClick}>
              Book Your Free 30-Minute Consultation
            </Button>

            <p className="text-sm md:text-base text-muted-dark leading-relaxed mt-4 italic">
              Physician-led care. Secure telemedicine. Available for Texas men.
            </p>
          </div>
        </div>
      </div>
    </section>;
}